import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Lock, Inbox as InboxIcon, Clock, Zap } from "lucide-react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import Footer from "../components/Footer";

// Contact page — auth-gated entry point into the internal messaging system.
// No SMTP, no mailto: anywhere. Every reply lands in /inbox.
//
// Formatting helper for the "avg reply" trust badge. Returns a friendly
// "4h", "27m", "1.2 days" string. Anything stale/invalid -> null and the
// badge is hidden entirely (we never want to surface a misleading number).
const formatResponseTime = (hours) => {
  if (hours == null || !isFinite(hours) || hours < 0) return null;
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 24) return `${hours.toFixed(hours < 5 ? 1 : 0)} hr${hours >= 1.5 ? 's' : ''}`;
  return `${(hours / 24).toFixed(1)} days`;
};
const ContactPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // Fire-and-forget — the page works fine if this never returns.
    api.get("/stats/support").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Please write a message.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/contact", {
        subject: subject.trim() || null,
        message: message.trim(),
      });
      toast.success("Message sent — replies will appear in your inbox.");
      navigate(`/inbox/${data.thread_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black text-gray-200 flex flex-col" data-testid="contact-page">
      <div className="container mx-auto px-4 py-12 max-w-2xl flex-1">
        <Link to="/" className="text-emerald-400 hover:text-emerald-300 text-sm">← Back home</Link>
        <h1 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-2">Contact us</h1>
        <p className="text-gray-400 text-sm mb-4">
          GlobalEyes.TV is operated by <strong className="text-gray-200">NETBINGE, LLC</strong>. To keep things spam-free and secure for everyone,
          support runs through our <strong className="text-gray-200">in-app inbox</strong> — no email needed, no inbox to leak.
        </p>

        {/* Trust signal — live SLA from the last 30 days. Hidden when there's
            not enough data to be honest. */}
        {stats && stats.thread_count >= 3 && (stats.median_first_response_hours != null || stats.reply_rate_pct != null) && (
          <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="contact-sla-badges">
            {stats.median_first_response_hours != null && (
              <span className="inline-flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-full px-3 py-1 text-xs"
                    data-testid="contact-sla-median">
                <Clock className="h-3 w-3" />
                Avg first reply: <strong className="text-emerald-200">{formatResponseTime(stats.median_first_response_hours)}</strong>
              </span>
            )}
            {stats.reply_rate_pct != null && stats.reply_rate_pct >= 80 && (
              <span className="inline-flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-full px-3 py-1 text-xs"
                    data-testid="contact-sla-rate">
                <Zap className="h-3 w-3" />
                {Math.round(stats.reply_rate_pct)}% of threads get a reply
              </span>
            )}
          </div>
        )}

        {!user ? (
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 text-center" data-testid="contact-auth-gate">
            <Lock className="h-8 w-8 text-emerald-400 mx-auto mb-3" />
            <h2 className="text-xl text-white font-semibold mb-2">Sign in to message support</h2>
            <p className="text-gray-400 text-sm mb-5">
              We only accept messages from registered accounts. Sign up takes ten seconds and comes with 20 starter credits.
            </p>
            <Link to="/">
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="contact-signin-cta">
                Go home & sign in
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-400 text-sm">
                Signed in as <span className="text-emerald-400">/{user.username}</span>
              </p>
              <Link to="/inbox" className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center gap-1.5" data-testid="contact-open-inbox">
                <InboxIcon className="h-4 w-4" /> Open inbox
              </Link>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div>
                <Label htmlFor="contact-subject" className="text-gray-300 text-sm">Subject (optional)</Label>
                <Input
                  id="contact-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Partnership / bug report / press inquiry…"
                  className="bg-gray-900 border-gray-700 text-white mt-1"
                  data-testid="contact-subject-input"
                />
              </div>
              <div>
                <Label htmlFor="contact-message" className="text-gray-300 text-sm">Message *</Label>
                <Textarea
                  id="contact-message"
                  required
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What's on your mind?"
                  className="bg-gray-900 border-gray-700 text-white mt-1"
                  data-testid="contact-message-input"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto"
                data-testid="contact-submit-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                {busy ? "Sending…" : "Send message"}
              </Button>
            </form>
          </>
        )}

        <div className="mt-12 pt-8 border-t border-gray-800">
          <p className="text-gray-400 text-sm">Why no email?</p>
          <p className="text-gray-500 text-xs mt-2 leading-relaxed">
            Email is a magnet for phishing and impersonation. Keeping every reply inside your authenticated inbox means you always know
            it's really us — and we always know it's really you. For abuse / DMCA / legal notices, open a thread here and a human will follow up.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ContactPage;
